import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoadingSpinner from '../common/LoadingSpinner';
import SessionExpiredModal from '../common/SessionExpiredModal';
import BookingTimer from './hotel-details/BookingTimer';
import { useBookingSession } from '../../hooks/useBookingSession';
import agentApiService from '../../services/agentApiService';

const AgentPassengerDetailsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL parameters
  const reserveCode = searchParams.get('reserveCode');
  const roomBookingCode = searchParams.get('roomBookingCode');
  const timerKey = searchParams.get('timerKey');
  const prebookKey = searchParams.get('prebookKey'); // Get prebookKey from URL

  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [roomBookingDisplayResponse, setRoomBookingDisplayResponse] = useState(null);
  const [hotelDetails, setHotelDetails] = useState(null); // NEW: Hotel details from cache
  const [hotelDetailsSearchCriteria, setHotelDetailsSearchCriteria] = useState(null); // NEW: Search criteria
  const [showAllHotelFacilities, setShowAllHotelFacilities] = useState(false); // For "See more" functionality
  const [showAllRoomFacilities, setShowAllRoomFacilities] = useState(false); // For "See more" functionality
  const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] = useState(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [timeoutProgress, setTimeoutProgress] = useState(0);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  // Use timerKey as expiryTime (timerKey contains the expiry timestamp)
  const expiryTime = timerKey ? parseInt(timerKey, 10) : null;
  const { isExpired, hasValidSession, getTimeRemaining } = useBookingSession(
    expiryTime,
    () => setShowSessionExpiredModal(true) // Show modal when session expires
  );

  // Fetch passenger summary data on component mount
  useEffect(() => {
    fetchPassengerSummary();
  }, []);

  const fetchPassengerSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);

      // Start timeout progress indicator
      const progressInterval = setInterval(() => {
        setTimeoutProgress(prev => {
          const newProgress = prev + (100 / 30); // 30 seconds total
          if (newProgress >= 50) { // Show warning at 15 seconds (50% progress)
            setShowTimeoutWarning(true);
          }
          return Math.min(newProgress, 100);
        });
      }, 1000);

      const response = await agentApiService.getAllPassengerDetails(
        reserveCode,
        roomBookingCode,
        timerKey,
        prebookKey // Pass prebookKey to API
      );

      // Clear progress interval
      clearInterval(progressInterval);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);

      if (response.success) {
        console.log('✅ Passenger summary received:', response);
        console.log('📦 roomCardDetails:', response.roomCardDetails);
        console.log('🏠 hotelDetails:', response.hotelDetails);
        console.log('🔍 hotelDetailsSearchCriteria:', response.hotelDetailsSearchCriteria);
        setBookingDetails(response.bookingDetails);
        setRoomBookingDisplayResponse(response.roomCardDetails); // Note: Backend returns as roomCardDetails
        setHotelDetails(response.hotelDetails); // NEW: Store hotel details
        setHotelDetailsSearchCriteria(response.hotelDetailsSearchCriteria); // NEW: Store search criteria
      } else {
        throw new Error(response.message || 'Failed to fetch passenger summary');
      }
    } catch (error) {
      console.error('Error fetching passenger summary:', error);
      setError(error.message);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);
      toast.error(`Failed to load passenger summary: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmBooking = async (isHoldBooking = false) => {
    // Check session validity before confirmation - show modal if expired
    if (isExpired || !hasValidSession) {
      setShowSessionExpiredModal(true);
      return;
    }

    try {
      setSubmitting(true);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);

      // Start timeout progress indicator
      const progressInterval = setInterval(() => {
        setTimeoutProgress(prev => {
          const newProgress = prev + (100 / 30); // 30 seconds total
          if (newProgress >= 50) { // Show warning at 15 seconds (50% progress)
            setShowTimeoutWarning(true);
          }
          return Math.min(newProgress, 100);
        });
      }, 1000);

      console.log('🔍 Mapping AgentHotelBookingResponseDto to BookingDetailsResponseDto');
      console.log('📥 Original bookingDetails:', bookingDetails);
      
      // Prepare booking details for submission - Map AgentHotelBookingResponseDto to BookingDetailsResponseDto
      const bookingData = {
        // Basic booking information
        agentId: bookingDetails.agentId,
        hotelName: bookingDetails.hotelName,
        checkInDate: bookingDetails.checkInDate,
        checkOutDate: bookingDetails.checkOutDate,
        city: bookingDetails.city,
        bookingDate: bookingDetails.bookingDate,
        address: bookingDetails.address,
        
        // Passenger details (using first passenger as primary)
        title: bookingDetails.passengerTitle,
        passengerFirstName: bookingDetails.passengerFirstName,
        passengerMiddleName: bookingDetails.passengerMiddleName,
        passengerLastName: bookingDetails.passengerLastName,
        passengerEmail: bookingDetails.passengerEmail,
        passengerPhone: bookingDetails.passengerPhoneNumber,
        age: bookingDetails.passengerAge,
        passengerPANCardNumber: bookingDetails.passengerPANCardNumber,
        
        // Room details
        roomType: bookingDetails.roomType,
        roomPrice: bookingDetails.roomPrice,
        totalPrice: bookingDetails.totalPrice,
        gstAmount: bookingDetails.gstAmount,
        platformFee: bookingDetails.platformFee,
        
        // Room allocations
        rooms: bookingDetails.rooms || [],
        
        // Status and provider (will be set by backend)
        status: 'PENDING', // Default status
        netAmount: bookingDetails.totalPrice - (bookingDetails.gstAmount || 0) - (bookingDetails.platformFee || 0),
        
        // Additional fields that might be needed
        phoneNumber: bookingDetails.passengerPhoneNumber,
        
        // GST Information (if available)
        ...(bookingDetails.gstNumber && bookingDetails.gstNumber !== 'NA' && bookingDetails.gstNumber !== '' && {
          gstNumber: bookingDetails.gstNumber,
          gstCompanyName: bookingDetails.gstCompanyName,
          gstCompanyAddress: bookingDetails.gstCompanyAddress,
          gstCompanyContactNumber: bookingDetails.gstCompanyContactNumber,
          gstCompanyEmail: bookingDetails.gstCompanyEmail
        }),
        
        // Price drop information (if available)
        isPriceDrop: false, // Default value
        newPrice: null // Default value
      };

      console.log('📤 Mapped bookingData for confirm booking:', bookingData);

      const response = await agentApiService.confirmBooking(
        reserveCode,
        roomBookingCode,
        isHoldBooking ? 'true' : 'false',
        bookingDetails.encodedRooms || '', // Use the encoded rooms from the response
        bookingData,
        prebookKey // Pass prebookKey to API
      );

      // Clear progress interval
      clearInterval(progressInterval);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);

      if (response.success) {
        // Check if async processing
        if (response.asyncProcessing) {
          // Show message about async processing
          toast.success(response.message, { autoClose: 5000 });
          toast.info('Your booking is being processed. Please check your panel in a few minutes.', { autoClose: 5000 });
        } else {
          // Normal success message
          toast.success(response.message || (isHoldBooking ? 'Booking held successfully!' : 'Booking confirmed successfully!'));
        }
        
        // Show success message and close window after delay
        setTimeout(() => {
          window.close();
        }, response.asyncProcessing ? 3000 : 2000);
      } else {
        if (response.showModal) {
          setShowInsufficientBalanceModal(true);
        } else {
          throw new Error(response.message || 'Failed to confirm booking');
        }
      }
    } catch (error) {
      console.error('Error confirming booking:', error);
      setTimeoutProgress(0);
      setShowTimeoutWarning(false);
      toast.error(`Failed to confirm booking: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <LoadingSpinner message="Loading passenger summary..." />
          
          {/* Timeout Progress Indicator */}
          {timeoutProgress > 0 && (
            <div className="mt-6">
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-1000 ${
                    showTimeoutWarning ? 'bg-yellow-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${timeoutProgress}%` }}
                ></div>
              </div>
              <p className={`text-sm ${showTimeoutWarning ? 'text-yellow-600' : 'text-gray-600'}`}>
                {showTimeoutWarning 
                  ? 'Taking longer than expected... Please wait.'
                  : 'Connecting to prebook service...'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Timeout in {Math.max(0, 30 - Math.floor(timeoutProgress * 30 / 100))} seconds
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Passenger Summary</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={fetchPassengerSummary}
            className="bg-red-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-red-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!bookingDetails) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Booking Details Available</h3>
          <p className="text-gray-600">Unable to load booking details.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Booking Timer */}
      <BookingTimer timerKey={timerKey} expiryTime={expiryTime} />

      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => window.close()}
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Booking Confirmation</h1>
                <p className="text-sm text-gray-600">Review and confirm your booking</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-blue-600 px-6 py-4">
            <h2 className="text-xl font-bold text-white">Booking Confirmation</h2>
          </div>

          <div className="p-6">
            {/* Success/Error Messages */}
            {showInsufficientBalanceModal && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-800 font-medium">Insufficient Balance</span>
                </div>
                <p className="text-red-700 mt-1">You don't have sufficient balance to complete this booking. Please recharge your wallet.</p>
              </div>
            )}

            {/* Booking Information */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-3">Booking Information</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <table className="w-full text-sm">
                  <tbody className="space-y-2">
                    <tr>
                      <td className="font-medium text-gray-700 py-1">Booking Date:</td>
                      <td className="text-gray-900 py-1">{bookingDetails.bookingDate || new Date().toLocaleDateString()}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700 py-1">Booking Time:</td>
                      <td className="text-gray-900 py-1">{bookingDetails.bookingTime || new Date().toLocaleTimeString()}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700 py-1">City:</td>
                      <td className="text-gray-900 py-1">{bookingDetails.city || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td className="font-medium text-gray-700 py-1">Agent ID:</td>
                      <td className="text-gray-900 py-1">{bookingDetails.agentId}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Hotel Information */}
            {hotelDetails && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 2L3 7v11a2 2 0 002 2h4v-6h2v6h4a2 2 0 002-2V7l-7-5z" />
                  </svg>
                  Hotel Information
                </h3>
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {hotelDetails.hotelName && (
                      <div>
                        <span className="text-sm text-gray-600">Hotel Name</span>
                        <p className="font-semibold text-gray-900">{hotelDetails.hotelName}</p>
                      </div>
                    )}
                    {hotelDetails.hotelId && (
                      <div>
                        <span className="text-sm text-gray-600">Hotel ID</span>
                        <p className="font-semibold text-gray-900">{hotelDetails.hotelId}</p>
                      </div>
                    )}
                    {hotelDetails.hotelStar && (
                      <div>
                        <span className="text-sm text-gray-600">Star Rating</span>
                        <div className="flex items-center mt-1">
                          {[...Array(hotelDetails.hotelStar)].map((_, i) => (
                            <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                          <span className="ml-2 text-sm text-gray-600">({hotelDetails.hotelStar} Star)</span>
                        </div>
                      </div>
                    )}
                    {hotelDetails.provider && (
                      <div>
                        <span className="text-sm text-gray-600">Provider</span>
                        <p className="font-semibold text-blue-600">{hotelDetails.provider}</p>
                      </div>
                    )}
                    {hotelDetailsSearchCriteria && hotelDetailsSearchCriteria.checkInDate && (
                      <div>
                        <span className="text-sm text-gray-600">Check-In Date</span>
                        <p className="font-semibold text-gray-900">{hotelDetailsSearchCriteria.checkInDate}</p>
                      </div>
                    )}
                    {hotelDetailsSearchCriteria && hotelDetailsSearchCriteria.checkOutDate && (
                      <div>
                        <span className="text-sm text-gray-600">Check-Out Date</span>
                        <p className="font-semibold text-gray-900">{hotelDetailsSearchCriteria.checkOutDate}</p>
                      </div>
                    )}
                    {hotelDetails.phoneNo && (
                      <div>
                        <span className="text-sm text-gray-600">Phone</span>
                        <p className="font-semibold text-gray-900">{hotelDetails.phoneNo}</p>
                      </div>
                    )}
                    {hotelDetails.email && (
                      <div>
                        <span className="text-sm text-gray-600">Email</span>
                        <p className="font-semibold text-gray-900">{hotelDetails.email}</p>
                      </div>
                    )}
                    {hotelDetails.faxNo && (
                      <div>
                        <span className="text-sm text-gray-600">Fax</span>
                        <p className="font-semibold text-gray-900">{hotelDetails.faxNo}</p>
                      </div>
                    )}
                    {hotelDetails.hotelWebsiteUrl && (
                      <div>
                        <span className="text-sm text-gray-600">Website</span>
                        <a href={hotelDetails.hotelWebsiteUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 hover:underline break-all">
                          {hotelDetails.hotelWebsiteUrl}
                        </a>
                      </div>
                    )}
                    {hotelDetails.checkInTime && (
                      <div>
                        <span className="text-sm text-gray-600">Check-In Time</span>
                        <p className="font-semibold text-green-600">{hotelDetails.checkInTime}</p>
                      </div>
                    )}
                    {hotelDetails.checkOutTime && (
                      <div>
                        <span className="text-sm text-gray-600">Check-Out Time</span>
                        <p className="font-semibold text-red-600">{hotelDetails.checkOutTime}</p>
                      </div>
                    )}
                  </div>

                  {hotelDetails.address && (
                    <div className="mb-4 pt-4 border-t">
                      <span className="text-sm text-gray-600 flex items-center mb-2">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        Address
                      </span>
                      {hotelDetails.address.completeAddress && (
                        <p className="text-gray-900 mb-2">{hotelDetails.address.completeAddress}</p>
                      )}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-700">
                        {hotelDetails.address.cityName && <div>City: {hotelDetails.address.cityName}</div>}
                        {hotelDetails.address.stateName && <div>State: {hotelDetails.address.stateName}</div>}
                        {hotelDetails.address.countryName && <div>Country: {hotelDetails.address.countryName}</div>}
                        {hotelDetails.address.pinCode && <div>PIN: {hotelDetails.address.pinCode}</div>}
                      </div>
                    </div>
                  )}

                  {hotelDetails.description && (
                    <div className="mb-4 pt-4 border-t">
                      <span className="text-sm text-gray-600 mb-2 block">Description</span>
                      <p className="text-gray-700 text-sm leading-relaxed">{hotelDetails.description}</p>
                    </div>
                  )}

                  {hotelDetails.hotelFacilities && hotelDetails.hotelFacilities.length > 0 && (
                    <div className="pt-4 border-t">
                      <span className="text-sm text-gray-600 mb-2 block">Facilities</span>
                      <div className="flex flex-wrap gap-2">
                        {(showAllHotelFacilities ? hotelDetails.hotelFacilities : hotelDetails.hotelFacilities.slice(0, 6)).map((facility, idx) => (
                          <span key={idx} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                            {facility}
                          </span>
                        ))}
                      </div>
                      {hotelDetails.hotelFacilities.length > 6 && (
                        <button
                          onClick={() => setShowAllHotelFacilities(!showAllHotelFacilities)}
                          className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          {showAllHotelFacilities ? 'See less' : `See more (${hotelDetails.hotelFacilities.length - 6} more)`}
                        </button>
                      )}
                    </div>
                  )}

                  {hotelDetails.termsAndConditions && hotelDetails.termsAndConditions.length > 0 && (
                    <div className="pt-4 border-t">
                      <span className="text-sm text-gray-600 mb-2 block">Hotel Terms & Conditions</span>
                      <ul className="space-y-1">
                        {hotelDetails.termsAndConditions.map((term, idx) => (
                          <li key={idx} className="text-gray-700 text-sm flex items-start">
                            <svg className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            {term}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            

            {/* Room-wise Passenger Allocation */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-3">Room-wise Passenger Allocation</h3>
              
              {bookingDetails.roomPassengerDetails && bookingDetails.roomPassengerDetails.map((roomDetail, roomIndex) => (
                <div key={roomIndex} className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                  {/* Room Header */}
                  <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z" />
                      </svg>
                      <span className="font-semibold text-gray-900">Room #{roomDetail.roomNumber}</span>
                    </div>
                  </div>

                  <div className="p-4">
                    {/* Adults Section */}
                    <h4 className="text-md font-semibold text-green-600 mb-3 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                      Adults
                    </h4>
                    
                    {roomDetail.adults && roomDetail.adults.map((adult, adultIndex) => (
                      <div key={adultIndex} className="mb-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            <span className="font-medium text-gray-700">Title:</span>
                            <span className="ml-1 text-gray-900">{adult.passengerTitle}</span>
                          </div>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            <span className="font-medium text-gray-700">First Name:</span>
                            <span className="ml-1 text-gray-900">{adult.passengerFirstName}</span>
                          </div>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            <span className="font-medium text-gray-700">Last Name:</span>
                            <span className="ml-1 text-gray-900">{adult.passengerLastName}</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                            </svg>
                            <span className="font-medium text-gray-700">Phone:</span>
                            <span className="ml-1 text-gray-900">{adult.passengerPhoneNumber}</span>
                          </div>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                            </svg>
                            <span className="font-medium text-gray-700">Email:</span>
                            <span className="ml-1 text-gray-900">{adult.passengerEmail}</span>
                          </div>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                            </svg>
                            <span className="font-medium text-gray-700">Country:</span>
                            <span className="ml-1 text-gray-900">{adult.passengerCountry}</span>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Children Section */}
                    <h4 className="text-md font-semibold text-yellow-600 mb-3 flex items-center mt-4">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zM8 6V5a2 2 0 114 0v1H8zm2 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                      </svg>
                      Children
                    </h4>
                    
                    {(!roomDetail.children || roomDetail.children.length === 0) ? (
                      <div className="text-gray-500 italic flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        No children in this room
                      </div>
                    ) : (
                      roomDetail.children.map((child, childIndex) => (
                        <div key={childIndex} className="mb-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                            <div className="flex items-center">
                              <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                              </svg>
                              <span className="font-medium text-gray-700">Title:</span>
                              <span className="ml-1 text-gray-900">{child.passengerTitle}</span>
                            </div>
                            <div className="flex items-center">
                              <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                              </svg>
                              <span className="font-medium text-gray-700">First Name:</span>
                              <span className="ml-1 text-gray-900">{child.passengerFirstName}</span>
                            </div>
                            <div className="flex items-center">
                              <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                              </svg>
                              <span className="font-medium text-gray-700">Last Name:</span>
                              <span className="ml-1 text-gray-900">{child.passengerLastName}</span>
                            </div>
                            <div className="flex items-center">
                              <svg className="w-4 h-4 text-gray-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                              </svg>
                              <span className="font-medium text-gray-700">Age:</span>
                              <span className="ml-1 text-gray-900">{child.passengerAge}</span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>



            {/* Cancellation Policy */}
            <div className="mb-6">
              <div className="flex items-center mb-3">
                <h3 className="font-bold text-red-600 text-lg flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                  </svg>
                  Cancellation Policy
                </h3>
                <hr className="flex-grow-1 ml-3 border-red-300" />
              </div>

              {(!roomBookingDisplayResponse?.room?.cancellationPolicies?.cancellationPolicy || roomBookingDisplayResponse.room.cancellationPolicies.cancellationPolicy.length === 0) ? (
                <div className="text-gray-500 italic pl-1 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  No cancellation policies available
                </div>
              ) : (
                roomBookingDisplayResponse.room.cancellationPolicies.cancellationPolicy.map((policy, policyIndex) => (
                  <div key={policyIndex} className="p-3 mb-3 bg-white border-l-4 border-red-500 shadow-sm rounded-r-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-semibold text-gray-700">From:</span>
                          <div className="font-bold text-blue-600 text-sm">
                            {policy.startDate ? new Date(policy.startDate).toLocaleString('en-IN') : 'N/A'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-semibold text-gray-700">To:</span>
                          <div className="font-bold text-blue-600 text-sm">
                            {policy.endDate ? new Date(policy.endDate).toLocaleString('en-IN') : 'N/A'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-semibold text-gray-700">Charge Type:</span>
                          <div className="font-bold text-gray-900 text-sm">{policy.chargeType || 'N/A'}</div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-semibold text-gray-700">Charge Value:</span>
                          <div className="font-bold text-green-600 text-lg">
                            {policy.chargeType === 'Fixed' 
                              ? `₹${new Intl.NumberFormat('en-IN').format(policy.chargeAmount || 0)}`
                              : `${policy.chargeAmount || 0}%`
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Rate Conditions */}
            <div className="mb-6">
              <div className="border-0 shadow-lg rounded-lg overflow-hidden bg-white">
                <div className="bg-gray-600 text-white font-bold px-4 py-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Rate Conditions
                </div>
                <div className="p-4 bg-gray-50">
                  {(!roomBookingDisplayResponse?.room?.termsAndConditions || roomBookingDisplayResponse.room.termsAndConditions.length === 0) ? (
                    <div className="text-gray-500 italic">
                      No rate conditions available.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {roomBookingDisplayResponse.room.termsAndConditions.map((condition, conditionIndex) => (
                        <div key={conditionIndex} className="bg-white border-l-4 border-gray-600 px-3 py-2 rounded-r shadow-sm">
                          <div className="flex items-start">
                            <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            {/* Check if condition contains HTML tags */}
                            {condition.includes('<') ? (
                              <div dangerouslySetInnerHTML={{ __html: condition }} />
                            ) : (
                              <span>{condition}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* GST Information */}
            {bookingDetails.gstNumber && bookingDetails.gstNumber !== 'NA' && bookingDetails.gstNumber !== '' && (
              <div className="mb-6">
                <div className="border-0 shadow-lg rounded-lg overflow-hidden bg-white">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold px-4 py-3 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                      <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                    </svg>
                    GST Details
                  </div>
                  <div className="p-4 bg-gray-50 space-y-3">
                    <div className="border-b border-gray-200 pb-2">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="font-semibold text-gray-700">GST Number:</span>
                        <span className="ml-2 font-semibold text-blue-600">{bookingDetails.gstNumber}</span>
                      </div>
                    </div>
                    
                    {bookingDetails.gstCompanyName && (
                      <div className="border-b border-gray-200 pb-2">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 text-gray-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-1a1 1 0 00-1-1H9a1 1 0 00-1 1v1a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                          </svg>
                          <span className="font-semibold text-gray-700">Company Name:</span>
                          <span className="ml-2 font-semibold">{bookingDetails.gstCompanyName}</span>
                        </div>
                      </div>
                    )}
                    
                    {bookingDetails.gstCompanyAddress && (
                      <div className="border-b border-gray-200 pb-2">
                        <div className="flex items-start">
                          <svg className="w-4 h-4 text-red-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                          <div>
                            <span className="font-semibold text-gray-700">Company Address:</span>
                            <div className="ml-0 font-semibold mt-1">{bookingDetails.gstCompanyAddress}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {bookingDetails.gstCompanyContactNumber && (
                      <div className="border-b border-gray-200 pb-2">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                          </svg>
                          <span className="font-semibold text-gray-700">Contact Number:</span>
                          <span className="ml-2 font-semibold">{bookingDetails.gstCompanyContactNumber}</span>
                        </div>
                      </div>
                    )}
                    
                    {bookingDetails.gstCompanyEmail && (
                      <div>
                        <div className="flex items-center">
                          <svg className="w-4 h-4 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                          </svg>
                          <span className="font-semibold text-gray-700">Email:</span>
                          <span className="ml-2 font-semibold">{bookingDetails.gstCompanyEmail}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Price Summary
            <div className="mb-6">
              <div className="bg-white shadow-sm border-0 rounded-lg overflow-hidden">
                <div className="bg-blue-600 text-white font-bold px-4 py-3">
                  Price Summary
                </div>
                <div className="p-4">
                  <table className="w-full border-collapse">
                    <tbody>
                      <tr className="border-b border-gray-200">
                        <th className="bg-gray-50 font-semibold text-left px-3 py-3 border border-gray-300">Total Price</th>
                        <td className="px-3 py-3 border border-gray-300">
                          <span className="text-2xl font-bold text-blue-600 flex items-center">
                            <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                            </svg>
                            ₹{bookingDetails.totalPriceFormatted || 
                              (bookingDetails.totalPrice ? 
                                new Intl.NumberFormat('en-IN').format(bookingDetails.totalPrice) : 'N/A')}
                          </span>
                        </td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <th className="bg-gray-50 font-semibold text-left px-3 py-3 border border-gray-300">Platform Fee</th>
                        <td className="px-3 py-3 border border-gray-300">
                          <span className="text-red-600 font-semibold flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                              <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                            </svg>
                            ₹{bookingDetails.platformFeeFormatted || 
                              (bookingDetails.platformFee ? 
                                new Intl.NumberFormat('en-IN').format(bookingDetails.platformFee) : '0')}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <th className="bg-gray-50 font-semibold text-left px-3 py-3 border border-gray-300">GST Amount</th>
                        <td className="px-3 py-3 border border-gray-300">
                          <span className="text-yellow-600 font-semibold flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                              <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                            </svg>
                            ₹{bookingDetails.gstAmountFormatted || 
                              (bookingDetails.gstAmount ? 
                                new Intl.NumberFormat('en-IN').format(bookingDetails.gstAmount) : '0')}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div> */}

{/* Room Details */}
            {roomBookingDisplayResponse && roomBookingDisplayResponse.room && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z" />
                  </svg>
                  Room Details
                </h3>
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* {roomBookingDisplayResponse.room.roomBookingCode && (
                      <div>
                        <span className="text-sm text-gray-600">Room Booking Code</span>
                        <p className="font-semibold text-gray-900">{roomBookingDisplayResponse.room.roomBookingCode}</p>
                      </div>
                    )} */}
                    {roomBookingDisplayResponse.room.roomCategory && roomBookingDisplayResponse.room.roomCategory.length > 0 && (
                      <div>
                        <span className="text-sm text-gray-600">Room Type</span>
                        <p className="font-semibold text-gray-900">{roomBookingDisplayResponse.room.roomCategory.join(', ')}</p>
                      </div>
                    )}
                    {roomBookingDisplayResponse.room.mealType && (
                      <div>
                        <span className="text-sm text-gray-600">Meal Type</span>
                        <p className="font-semibold text-orange-600">{roomBookingDisplayResponse.room.mealType}</p>
                      </div>
                    )}
                    {roomBookingDisplayResponse.room.inclusion && (
                      <div>
                        <span className="text-sm text-gray-600">Inclusion</span>
                        <p className="font-semibold text-gray-900">{roomBookingDisplayResponse.room.inclusion}</p>
                      </div>
                    )}
                    {roomBookingDisplayResponse.room.isRefundable !== undefined && (
                      <div>
                        <span className="text-sm text-gray-600">Refundable</span>
                        <p className={`font-semibold ${roomBookingDisplayResponse.room.isRefundable ? 'text-green-600' : 'text-red-600'}`}>
                          {roomBookingDisplayResponse.room.isRefundable ? 'Yes' : 'No'}
                        </p>
                      </div>
                    )}
                    {roomBookingDisplayResponse.room.withTransfer !== undefined && (
                      <div>
                        <span className="text-sm text-gray-600">With Transfer</span>
                        <p className={`font-semibold ${roomBookingDisplayResponse.room.withTransfer ? 'text-green-600' : 'text-gray-600'}`}>
                          {roomBookingDisplayResponse.room.withTransfer ? 'Yes' : 'No'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Price Breakdown */}
                  {roomBookingDisplayResponse.room.roomFareDetails && (
                    <div className="mb-4 pt-4 border-t">
                      <span className="text-sm text-gray-600 mb-3 block flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                        </svg>
                        Price Breakdown
                      </span>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        {roomBookingDisplayResponse.room.roomFareDetails.isPriceChanged && (
                          <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                            <p className="text-yellow-800 text-sm font-semibold flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              Price has changed!
                            </p>
                          </div>
                        )}
                        
                        {/* {roomBookingDisplayResponse.room.roomFareDetails.bookingBeforePrice !== undefined && roomBookingDisplayResponse.room.roomFareDetails.bookingBeforePrice !== null && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700">Original Price:</span>
                            <span className="text-gray-900 font-semibold">₹{new Intl.NumberFormat('en-IN').format(roomBookingDisplayResponse.room.roomFareDetails.bookingBeforePrice)}</span>
                          </div>
                        )} */}
                        
                        {roomBookingDisplayResponse.room.roomFareDetails.bookingAfterPrice !== undefined && roomBookingDisplayResponse.room.roomFareDetails.bookingAfterPrice !== null && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700">Room Price:</span>
                            <span className="text-blue-600 font-bold text-lg">₹{new Intl.NumberFormat('en-IN').format(roomBookingDisplayResponse.room.roomFareDetails.bookingAfterPrice)}</span>
                          </div>
                        )}
                        
                        {roomBookingDisplayResponse.room.roomFareDetails.platFormFee !== undefined && roomBookingDisplayResponse.room.roomFareDetails.platFormFee !== null && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700">Platform Fee:</span>
                            <span className="text-gray-900 font-semibold">₹{new Intl.NumberFormat('en-IN').format(roomBookingDisplayResponse.room.roomFareDetails.platFormFee)}</span>
                          </div>
                        )}
                        
                        {roomBookingDisplayResponse.room.roomFareDetails.gstAmountOnPlatFormFee !== undefined && roomBookingDisplayResponse.room.roomFareDetails.gstAmountOnPlatFormFee !== null && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700">
                              GST on Platform Fee
                              {roomBookingDisplayResponse.room.roomFareDetails.gstPercentageOnPlatFormFee && 
                                ` (${roomBookingDisplayResponse.room.roomFareDetails.gstPercentageOnPlatFormFee}%)`
                              }:
                            </span>
                            <span className="text-gray-900 font-semibold">₹{new Intl.NumberFormat('en-IN').format(roomBookingDisplayResponse.room.roomFareDetails.gstAmountOnPlatFormFee)}</span>
                          </div>
                        )}
                        
                        {roomBookingDisplayResponse.room.roomFareDetails.differenceBetweenFare !== undefined && 
                         roomBookingDisplayResponse.room.roomFareDetails.differenceBetweenFare !== null && 
                         roomBookingDisplayResponse.room.roomFareDetails.differenceBetweenFare !== 0 && (
                          <div className={`flex justify-between items-center p-2 rounded ${
                            roomBookingDisplayResponse.room.roomFareDetails.differenceBetweenFare < 0 ? 'bg-green-100' : 'bg-red-100'
                          }`}>
                            <span className={`font-semibold ${
                              roomBookingDisplayResponse.room.roomFareDetails.differenceBetweenFare < 0 ? 'text-green-700' : 'text-red-700'
                            }`}>
                              {roomBookingDisplayResponse.room.roomFareDetails.differenceBetweenFare < 0 ? 'Discount:' : 'Additional Charge:'}
                            </span>
                            <span className={`font-bold ${
                              roomBookingDisplayResponse.room.roomFareDetails.differenceBetweenFare < 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {roomBookingDisplayResponse.room.roomFareDetails.differenceBetweenFare < 0 ? '-' : '+'}₹{new Intl.NumberFormat('en-IN').format(Math.abs(roomBookingDisplayResponse.room.roomFareDetails.differenceBetweenFare))}
                            </span>
                          </div>
                        )}
                        
                        <div className="pt-2 mt-2 border-t-2 border-gray-300">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-900 font-bold text-lg">Total Amount:</span>
                            <span className="text-green-600 font-bold text-2xl">
                              ₹{new Intl.NumberFormat('en-IN').format(
                                (roomBookingDisplayResponse.room.roomFareDetails.bookingAfterPrice || 0) + 
                                (roomBookingDisplayResponse.room.roomFareDetails.platFormFee || 0) + 
                                (roomBookingDisplayResponse.room.roomFareDetails.gstAmountOnPlatFormFee || 0)
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {roomBookingDisplayResponse.room.roomDescription && (
                    <div className="mb-4 pt-4 border-t">
                      <span className="text-sm text-gray-600 mb-2 block">Room Description</span>
                      <p className="text-gray-700 text-sm leading-relaxed">{roomBookingDisplayResponse.room.roomDescription}</p>
                    </div>
                  )}

                  {roomBookingDisplayResponse.room.roomFacilities && roomBookingDisplayResponse.room.roomFacilities.length > 0 && (
                    <div className="mb-4 pt-4 border-t">
                      <span className="text-sm text-gray-600 mb-2 block">Room Facilities</span>
                      <div className="flex flex-wrap gap-2">
                        {(showAllRoomFacilities ? roomBookingDisplayResponse.room.roomFacilities : roomBookingDisplayResponse.room.roomFacilities.slice(0, 6)).map((facility, idx) => (
                          <span key={idx} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                            {facility}
                          </span>
                        ))}
                      </div>
                      {roomBookingDisplayResponse.room.roomFacilities.length > 6 && (
                        <button
                          onClick={() => setShowAllRoomFacilities(!showAllRoomFacilities)}
                          className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          {showAllRoomFacilities ? 'See less' : `See more (${roomBookingDisplayResponse.room.roomFacilities.length - 6} more)`}
                        </button>
                      )}
                    </div>
                  )}

                  {roomBookingDisplayResponse.room.roomPromotion && roomBookingDisplayResponse.room.roomPromotion.length > 0 && (
                    <div className="mb-4 pt-4 border-t">
                      <span className="text-sm text-gray-600 mb-2 block flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                        Promotions
                      </span>
                      <ul className="space-y-1">
                        {roomBookingDisplayResponse.room.roomPromotion.map((promo, idx) => (
                          <li key={idx} className="text-green-700 text-sm flex items-start">
                            <svg className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            {promo}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {roomBookingDisplayResponse.room.termsAndConditions && roomBookingDisplayResponse.room.termsAndConditions.length > 0 && (
                    <div className="pt-4 border-t">
                      <span className="text-sm text-gray-600 mb-2 block">Room Terms & Conditions</span>
                      <ul className="space-y-1">
                        {roomBookingDisplayResponse.room.termsAndConditions.map((term, idx) => (
                          <li key={idx} className="text-gray-700 text-sm flex items-start">
                            <svg className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            {term}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-center gap-4 mt-6 flex-wrap">
              {/* Confirm Booking */}
              <button
                onClick={() => handleConfirmBooking(false)}
                disabled={submitting || isExpired || !hasValidSession}
                className={`px-8 py-3 rounded-lg font-medium transition-colors ${
                  submitting || isExpired || !hasValidSession
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {submitting ? (
                  <div className="flex items-center space-x-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Processing...</span>
                    {showTimeoutWarning && (
                      <span className="text-yellow-200 text-xs">
                        ({Math.max(0, 30 - Math.floor(timeoutProgress * 30 / 100))}s)
                      </span>
                    )}
                  </div>
                ) : 'Confirm Booking'}
              </button>

              {/* Hold Booking */}
              {bookingDetails.isRefundable && (
                <button
                  onClick={() => handleConfirmBooking(true)}
                  disabled={submitting || isExpired || !hasValidSession}
                  className={`px-8 py-3 rounded-lg font-medium transition-colors ${
                    submitting || isExpired || !hasValidSession
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-yellow-500 text-white hover:bg-yellow-600'
                  }`}
                >
                  {submitting ? (
                    <div className="flex items-center space-x-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Processing...</span>
                      {showTimeoutWarning && (
                        <span className="text-yellow-200 text-xs">
                          ({Math.max(0, 30 - Math.floor(timeoutProgress * 30 / 100))}s)
                        </span>
                      )}
                    </div>
                  ) : 'Hold Booking'}
                </button>
              )}

              {/* Cancel */}
              <button
                onClick={() => window.close()}
                disabled={submitting}
                className="px-8 py-3 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Session Expired Modal */}
      <SessionExpiredModal
        isOpen={showSessionExpiredModal}
        onClose={() => setShowSessionExpiredModal(false)}
      />
    </div>
  );
};

export default AgentPassengerDetailsPage;
